import React, { useState, useEffect } from "react";
import { api, fileUrl } from "@/lib/api";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ProjectVisual from "@/components/ProjectVisual";
import { ArrowUpRight, X } from "lucide-react";
import SEO from "@/components/SEO";

const CATEGORIES = [
  { id: "all", label: "Tutti" },
  { id: "ristrutturazioni", label: "Ristrutturazioni" },
  { id: "edificazioni", label: "Edificazioni" },
  { id: "operazioni_immobiliari", label: "Operazioni" },
];

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/portfolio", { params: filter === "all" ? {} : { category: filter } })
      .then((r) => setProjects(r.data || []))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div>
      <SEO
        title="Portfolio progetti — Ristrutturazioni ed edificazioni a Napoli | Ri.Sco."
        description="Progetti Ri.Sco. a Napoli e provincia: ristrutturazioni di pregio, rifacimenti facciate, edificazioni e riqualificazioni. Guarda il portfolio e richiedi un sopralluogo gratuito."
        path="/portfolio"
      />
      <section className="pt-32 pb-16 md:pt-40 md:pb-20" data-testid="portfolio-hero">
        <div className="container-x">
          <div className="overline mb-6">Portfolio · una selezione</div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl lg:text-9xl tracking-tighter text-risco-ink leading-[0.95]">
            <span className="font-display italic">I nostri</span> progetti
          </h1>
          <p className="text-stone-600 mt-8 max-w-2xl text-lg leading-relaxed">
            Una <strong className="text-risco-ink">selezione</strong> di interventi <strong className="text-risco-ink">realizzati e attualmente in corso di realizzazione</strong> a Napoli e in Campania. Ogni progetto raccontato dal prima al dopo.
          </p>
        </div>
      </section>

      <section className="pb-8" data-testid="portfolio-filters">
        <div className="container-x flex flex-wrap gap-2 border-y border-risco-border py-6">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              data-testid={`portfolio-filter-${c.id}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === c.id
                  ? "bg-risco-ink text-risco-bg"
                  : "text-stone-700 hover:bg-risco-alt"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section className="pb-32" data-testid="portfolio-grid">
        <div className="container-x">
          {loading ? (
            <div className="text-stone-500">Caricamento...</div>
          ) : projects.length === 0 ? (
            <div className="text-stone-500">Nessun progetto in questa categoria.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16">
              {projects.map((p, i) => {
                const hasImage = !!(p.after_image || p.before_image);
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelected(p)}
                    data-testid={`portfolio-project-${p.id}`}
                    className="risco-card-plain group text-left"
                  >
                    <div className={`card-img-wrap bg-stone-200 ${i % 3 === 1 ? "aspect-[3/4]" : "aspect-[4/5]"}`}>
                      {hasImage ? (
                        <img
                          src={fileUrl(p.after_image || p.before_image)}
                          alt={p.title}
                          className="card-img w-full h-full object-cover"
                        />
                      ) : (
                        <ProjectVisual project={p} />
                      )}
                    </div>
                    <div className="pt-5 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-risco-accent">{p.category?.replace("_", " ")}</div>
                        <div className="font-heading text-xl md:text-2xl text-risco-ink mt-2 tracking-tight">{p.title}</div>
                        <div className="text-sm text-stone-500 mt-2 flex items-center gap-2 flex-wrap">
                          <span>{p.location}</span>
                          {p.year ? (
                            <span>— {p.year}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-risco-accent/12 text-risco-accent text-[10px] uppercase tracking-[0.16em] font-semibold">
                              <span className="w-1.5 h-1.5 rounded-full bg-risco-accent animate-pulse" />
                              In fase di ultimazione
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-9 h-9 rounded-full border border-stone-400 flex items-center justify-center shrink-0 group-hover:bg-risco-ink group-hover:border-risco-ink transition-colors">
                        <ArrowUpRight className="w-3.5 h-3.5 text-risco-ink group-hover:text-risco-bg transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && projects.length > 0 && (
            <div className="mt-24 md:mt-32 border-t border-risco-border pt-12" data-testid="portfolio-note">
              <div className="max-w-3xl">
                <div className="overline mb-4">Nota</div>
                <p className="font-display italic text-2xl md:text-3xl text-risco-ink leading-snug">
                  Quelli mostrati sono solo <span className="not-italic font-heading">alcuni</span> dei nostri lavori.
                </p>
                <p className="text-stone-600 mt-5 text-base md:text-lg leading-relaxed">
                  Molti altri progetti — ristrutturazioni, edificazioni e operazioni immobiliari — sono stati portati a termine e consegnati ai clienti nel <strong className="text-risco-ink">perfetto rispetto delle tempistiche</strong> e delle specifiche di progetto concordate.
                </p>
                <p className="text-stone-600 mt-3 text-sm">
                  Per una panoramica completa o referenze specifiche, contattaci direttamente.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 bg-risco-ink/85 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto py-10 px-4" onClick={() => setSelected(null)} data-testid="portfolio-modal">
          <div className="bg-risco-bg max-w-5xl w-full p-8 md:p-14 relative rounded-sm" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} data-testid="portfolio-modal-close" className="absolute top-6 right-6 w-10 h-10 rounded-full border border-stone-400 hover:bg-risco-ink hover:text-risco-bg text-risco-ink flex items-center justify-center transition-colors">
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.22em] text-risco-accent">{selected.category?.replace("_", " ")}</div>
            <h2 className="font-heading text-3xl md:text-5xl text-risco-ink mt-3 tracking-tight">{selected.title}</h2>
            <div className="text-sm text-stone-600 mt-2">{selected.location}{selected.year ? ` — ${selected.year}` : ""}</div>
            {selected.before_image && selected.after_image ? (
              <div className="mt-8">
                <BeforeAfterSlider before={fileUrl(selected.before_image)} after={fileUrl(selected.after_image)} height={480} />
              </div>
            ) : (
              <div className="mt-8 aspect-[16/9] rounded-sm overflow-hidden">
                <ProjectVisual project={selected} />
              </div>
            )}
            <p className="text-stone-700 mt-8 leading-relaxed max-w-3xl">{selected.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}
